// lib/ab/index.js
// Pruebas A/B: lo que usa el resto de la web. El sorteo NO está acá: lo hace el script inline
// del <head> (lib/ab/script.js), que corre antes de que se dibuje la página y deja el estado
// en `window.__bcmAb`. Esto solo lo lee y avisa al servidor.
//
//   variante("l01")    la variante que le tocó a este visitante en esa prueba, o null
//   getAb()            el texto de la cookie, para mandar en el checkout y en las altas
//   asignarRuta(path)  sortea lo que corra en esa página (navegación sin recarga)
//   anotarRuta(path)   avisa al servidor los pasos de esa página
//
// Los resultados se ven en el admin, Analytics > Pruebas A/B. De dónde sale cada paso:
//   visita     vio la página de la prueba   anotarRuta()  -> beacon -> ab_eventos
//   checkout   abrió el checkout            anotarRuta()  -> beacon -> ab_eventos
//   mail       escribió el mail             getAb() en /api/checkout-lead -> ab_mails
//   muestra    pidió la muestra             getAb() en /api/newsletter    -> ab_mails
//   pago       tocó Pagar                   getAb() en ad.ab del checkout -> ab_checkouts
//   compra     la venta                     ab_checkouts -> fb_ads_params -> sales
//
// TODO ES OPCIONAL Y NADA TIRA. Si el script del head no corrió (crawler, cookies bloqueadas,
// un error), `window.__bcmAb` no existe: variante() y getAb() devuelven null, los avisos no
// salen y la web anda exactamente como antes de que existieran las pruebas. Estas funciones
// corren adentro del checkout: ninguna puede lanzar una excepción.

import { leerPais } from "@/lib/geo/pais";
import { COOKIE_RE, PAGINAS } from "./config";

const RUTA_REPORTE = "/api/ab-evento";
const CLAVE_SESION = "bcm_ab_anotados";
const ES_CHECKOUT = new RegExp(PAGINAS.checkout);

function estado() {
  if (typeof window === "undefined") return null;
  const ab = window.__bcmAb;
  return ab && typeof ab === "object" && ab.e && typeof ab.asignar === "function" ? ab : null;
}

/**
 * La variante de este visitante en una prueba, o null si no está en ella.
 * En el servidor y en el primer render devuelve null: una variante que cambia lo que se ve
 * tiene que armarse con CSS sobre html[data-ab-<clave>] para no parpadear (ver config.js).
 */
export function variante(clave) {
  try {
    const v = estado()?.e?.a?.[clave];
    return typeof v === "string" ? v : null;
  } catch {
    return null;
  }
}

/**
 * El texto de la cookie (`<visitante>~<clave>:<variante>...`) para mandar al servidor, o null.
 * Se valida contra el mismo formato que revisa el back: lo que no lo cumple no viaja.
 */
export function getAb() {
  try {
    const texto = estado()?.texto?.();
    return typeof texto === "string" && COOKIE_RE.test(texto) ? texto : null;
  } catch {
    return null;
  }
}

/**
 * Sortea las pruebas que corren en esta página para el que llegó navegando sin recargar (ahí
 * el script del head no vuelve a correr). Devuelve las claves de esas pruebas.
 */
export function asignarRuta(pathname) {
  try {
    const en = estado()?.asignar(String(pathname || ""));
    return Array.isArray(en) ? en : [];
  } catch {
    return [];
  }
}

// Una vez por (prueba, paso) y por sesión de pestaña: el back deduplica igual por persona,
// esto solo evita un beacon por cada recarga.
const anotados = new Set();

function esNuevo(clave) {
  if (anotados.has(clave)) return false;
  anotados.add(clave);

  try {
    const previos = String(window.sessionStorage.getItem(CLAVE_SESION) || "")
      .split("|")
      .filter(Boolean);
    if (previos.includes(clave)) return false;
    previos.push(clave);
    window.sessionStorage.setItem(CLAVE_SESION, previos.slice(-24).join("|"));
  } catch {
    /* sessionStorage bloqueado: alcanza con el Set */
  }
  return true;
}

/** Solo para tests: el Set vive lo que vive el módulo. */
export function _resetAnotados() {
  anotados.clear();
}

/** "m" para celular o tablet, "d" para escritorio. El iPad moderno se presenta como una Mac. */
function dispositivo() {
  const ua = String(navigator.userAgent || "");
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return "m";
  return /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1 ? "m" : "d";
}

/**
 * El path con el idioma adelante. La web muestra /libros un instante antes de corregirlo a
 * /es/libros (LocaleUrlSync), y este aviso puede salir en ese instante.
 */
function rutaConIdioma() {
  const ruta = String(window.location.pathname || "").slice(0, 300);
  const idioma = String(document.documentElement.lang || "");
  if (!/^[a-z]{2}$/.test(idioma) || ruta === `/${idioma}` || ruta.startsWith(`/${idioma}/`)) return ruta;
  return `/${idioma}${ruta === "/" ? "" : ruta}`.slice(0, 300);
}

function enviar(ab, pasos) {
  const campos = {
    v: ab.e.v,
    f: ab.e.f ? "1" : "0",
    pasos: pasos.map((p) => p.join(":")).join("."),
    ruta: rutaConIdioma(),
    d: dispositivo(),
  };
  // El país solo si se sabe de verdad: el fallback es siempre RW y no dice nada.
  const { pais, fuente } = leerPais();
  if ((fuente === "cc" || fuente === "geo") && pais) campos.pais = pais;

  const cuerpo = new URLSearchParams(campos).toString();

  // text/plain: no dispara preflight de CORS y sobrevive a que la pestaña se cierre.
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    navigator.sendBeacon(RUTA_REPORTE, new Blob([cuerpo], { type: "text/plain;charset=UTF-8" }));
    return;
  }
  fetch(RUTA_REPORTE, {
    method: "POST",
    body: cuerpo,
    headers: { "Content-Type": "text/plain;charset=UTF-8" },
    keepalive: true,
  }).catch(() => {});
}

/**
 * Avisa al servidor lo que pasó en esta página, en UN solo pedido:
 *   "visita"    por cada prueba que corre acá (el visitante entró a la prueba);
 *   "checkout"  por cada prueba en la que ya está, si esta página es el checkout.
 * Llamar en cada cambio de página (components/analytics/PruebasAb.jsx).
 */
export function anotarRuta(pathname) {
  try {
    const ab = estado();
    if (!ab) return;

    const ruta = String(pathname || "");
    const asignadas = ab.e.a || {};
    const pasos = [];
    for (const clave of asignarRuta(ruta)) {
      if (asignadas[clave]) pasos.push([clave, asignadas[clave], "visita"]);
    }
    if (ES_CHECKOUT.test(ruta)) {
      for (const clave of Object.keys(asignadas)) pasos.push([clave, asignadas[clave], "checkout"]);
    }

    const nuevos = pasos.filter(([clave, , evento]) => esNuevo(`${clave}:${evento}`)).slice(0, 8);
    if (nuevos.length && ab.e.v) enviar(ab, nuevos);
  } catch {
    /* nunca romper la página por el aviso */
  }
}
